/**
 * TJP Online 2P client plumbing: gateway room API, the room WebSocket, and
 * snapshot interpolation. Server is authoritative (general_services/tjp_rooms.py on
 * the generic room layer, online_rooms.py; socket/helpers in onlineRoomsNet.js);
 * this client only sends its own seat's action and renders server snapshots.
 */
import { RoomSocket, authHeaders, gatewayHttpBase, gatewayWsUrl } from './onlineRoomsNet';

export { authHeaders, gatewayHttpBase, gatewayWsUrl };

/** Render this far behind the newest snapshot (≈ 2.5 snapshots at 25 Hz). */
export const INTERP_DELAY_MS = 100;
const BUFFER_MAX = 30;

const LERP_KEYS = [
  'cart_position', 'cart_velocity', 'pole1_angle', 'pole1_velocity',
  'pole2_angle', 'pole2_velocity', 'punch_state',
];

async function call(method, path, body) {
  const res = await fetch(`${gatewayHttpBase()}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) {
    const err = new Error((data && (data.error || data.code)) || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data && data.code;
    throw err;
  }
  return data;
}

export const tjpRooms = {
  list: () => call('GET', '/api/tjp/rooms').then((d) => (d && d.rooms) || []),
  create: (opts = {}) => call('POST', '/api/tjp/rooms', opts),
  match: (opts = {}) => call('POST', '/api/tjp/rooms/match', opts),
  join: (roomId) => call('POST', `/api/tjp/rooms/${encodeURIComponent(roomId)}/join`, {}),
  info: (roomId) => call('GET', `/api/tjp/rooms/${encodeURIComponent(roomId)}`),
  leave: (roomId, seatToken) => call('POST', `/api/tjp/rooms/${encodeURIComponent(roomId)}/leave`, { seat_token: seatToken }),
};

/** Fire-and-forget leave that survives page unload. */
export function beaconLeave(roomId, seatToken) {
  const url = `${gatewayHttpBase()}/api/tjp/rooms/${encodeURIComponent(roomId)}/leave`;
  const body = JSON.stringify({ seat_token: seatToken });
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
  } catch (_) { /* fall through */ }
  try { fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }); } catch (_) { /* ignore */ }
}

// ---- seat tokens survive a reload (same tab) so the seat can reconnect
const SEAT_KEY = 'evlx_tjp_online_seat';
export function saveSeat(grant) {
  try { sessionStorage.setItem(SEAT_KEY, JSON.stringify({ room_id: grant.room_id, seat: grant.seat, seat_token: grant.seat_token, ws_path: grant.ws_path })); } catch (_) { /* ignore */ }
}
export function loadSeat() {
  try { return JSON.parse(sessionStorage.getItem(SEAT_KEY) || 'null'); } catch (_) { return null; }
}
export function clearSeat() {
  try { sessionStorage.removeItem(SEAT_KEY); } catch (_) { /* ignore */ }
}

export function roomIdFromSearch(search) {
  const m = /[?&]tjp_room=([A-Za-z0-9_-]{1,64})/.exec(search || '');
  return m ? m[1] : null;
}

export function inviteLink(roomId, loc = window.location) {
  const url = new URL(loc.href);
  url.searchParams.set('tjp_room', roomId);
  return url.toString();
}

// ---- snapshot interpolation
export function createSnapshotBuffer() {
  return { items: [] };
}

export function pushSnapshot(buf, snap, receivedAt) {
  const items = buf.items;
  if (items.length && snap.tick <= items[items.length - 1].snap.tick) return buf;
  items.push({ t: receivedAt, snap });
  if (items.length > BUFFER_MAX) items.splice(0, items.length - BUFFER_MAX);
  return buf;
}

function lerpPhysics(a = {}, b = {}, f) {
  const out = { ...b };
  LERP_KEYS.forEach((k) => {
    const x = Number(a[k]);
    const y = Number(b[k]);
    if (Number.isFinite(x) && Number.isFinite(y)) out[k] = x + (y - x) * f;
  });
  return out;
}

/**
 * Interpolated { player_1, player_2 } physics at renderAt (ms, same clock as
 * receivedAt). Holds the newest snapshot when the buffer runs dry (no extrapolation).
 */
export function sampleSnapshot(buf, renderAt) {
  const items = buf.items;
  if (!items.length) return null;
  if (renderAt <= items[0].t) return physicsOf(items[0].snap);
  for (let i = items.length - 1; i > 0; i -= 1) {
    const a = items[i - 1];
    const b = items[i];
    if (renderAt >= a.t && renderAt <= b.t) {
      const span = b.t - a.t;
      const f = span > 0 ? (renderAt - a.t) / span : 1;
      const pa = physicsOf(a.snap);
      const pb = physicsOf(b.snap);
      return {
        player_1: lerpPhysics(pa.player_1, pb.player_1, f),
        player_2: lerpPhysics(pa.player_2, pb.player_2, f),
      };
    }
  }
  return physicsOf(items[items.length - 1].snap);
}

function physicsOf(snap) {
  const p = (snap && snap.players) || {};
  return {
    player_1: (p.player_1 && p.player_1.physics) || {},
    player_2: (p.player_2 && p.player_2.physics) || {},
  };
}

/** Map held keys to the one action this seat sends (punch is sent separately). */
export function heldAction(keys) {
  const left = keys.has('ArrowLeft') || keys.has('a') || keys.has('A');
  const right = keys.has('ArrowRight') || keys.has('d') || keys.has('D');
  if (left && !right) return 'left';
  if (right && !left) return 'right';
  return 'stay';
}

/** TJP room socket: the generic RoomSocket bound to game "tjp" (/ws/tjp/<id>). */
export class TjpRoomSocket extends RoomSocket {
  constructor(grant, opts = {}) {
    super(grant, { ...opts, game: 'tjp' });
  }
}
